/**
 * whiteLabelManagerLib.js
 * Pure constants and helpers for the Admin WhiteLabelManager (WhiteLabelManager.js).
 * Extracted for maintainability — no React imports, no side effects.
 */
import {
  createSiteTemplatePage,
  DEFAULT_SITE_DESIGN_NAMES,
} from '@/lib/siteDesigns';
import {
  DEFAULT_SHARED_STOREFRONT_CONTENT,
} from '@/lib/whiteLabelSharedContent';

// ── Demo partner data (UI placeholder) ───────────────────────────────────────
export const INITIAL_PARTNERS = [
  { name: 'Eagle Hosting', url: 'eagle.cloudsetu.com', status: 'Published', updated: '2026-10-28' },
  { name: 'Northstar Cloud', url: 'northstar.cloudsetu.com', status: 'Draft', updated: '2026-10-27' },
  { name: 'CloudNova', url: 'cloudnova.cloudsetu.com', status: 'Published', updated: '2026-10-25' },
  { name: 'PixelServer', url: '', status: 'Not Started', updated: '-' },
];

// INITIAL_SECTIONS is now served from backend (/api/admin/white-label/section-library)
// This is kept as a local fallback only — the backend is the source of truth
export const INITIAL_SECTIONS = [
  { type: 'header', name: 'Header', variants: ['Logo Left / Navigation Right', 'Centered Navigation', 'Centered Brand', 'Announcement Bar', 'Minimal Header', 'Split Navigation', 'Mobile-first Header'], tiers: 'All tiers', enabled: true },
  { type: 'hero', name: 'Hero Section (Banner)', variants: ['3D Isometric Hosting Cloud', 'Split Tech Architecture', 'Centered Cyber Tech Hub', 'Server Stats Showcase', 'Dark Glow Infrastructure', 'Performance Split Hero', 'Conversion Centered Hero'], tiers: 'All tiers', enabled: true },
  { type: 'why-us', name: 'Why Choose Us', variants: ['3-Card Illustration Grid', 'Split Feature Showcase', 'Hover Glow Badges & Cards', 'Dark Tech Glassmorphism', 'Horizontal Benefit Rows', 'Benefit Card Grid', 'Operations Advantage Split'], tiers: 'All tiers', enabled: true },
  { type: 'features', name: 'Features', variants: ['3-Column Cards', '2-Column Split Image', 'Icon Row List', 'Bento Grid', 'Timeline Steps', 'Platform Bento Grid', 'Feature Checklist'], tiers: 'All tiers', enabled: true },
  { type: 'pricing', name: 'Pricing', variants: ['Three Tier Cards', 'Comparison Matrix Table', 'Compact Plan List', 'Monthly / Annual Switch', 'Featured Spotlight', 'Plan Comparison Cards', 'Guided Plan Selection'], tiers: 'All tiers', enabled: true },
  { type: 'testimonials', name: 'Testimonials', variants: ['Three Column Cards', 'Spotlight Case Study', 'Quote Grid Wall', 'Trust Badges & Review Bar', 'Social Feed Cards', 'Customer Story Cards', 'Featured Customer Story'], tiers: 'Premium', enabled: true },
  { type: 'trust', name: 'Trust Logos', variants: ['Minimal Logo Strip', '2-Row Logo Grid', 'Metrics & Certifications', 'Badge Cards Row', 'Global Reach Counter', 'Trust Pillars', 'Service Standards Grid'], tiers: 'All tiers', enabled: true },
  { type: 'faq', name: 'FAQs', variants: ['Accordion List', 'Two Column Questions', 'Centered FAQ Cards', 'Category Tabs', 'Minimal Search & Answers', 'Simple FAQ List', 'FAQ Split Panel'], tiers: 'All tiers', enabled: true },
  { type: 'blog', name: 'Blogs', variants: ['3-Column Article Cards', 'Featured Story + Grid', 'Magazine Grid', 'Horizontal Article List', 'Newsroom Timeline', 'Editorial Feature Grid', 'Latest Insights List'], tiers: 'All tiers', enabled: true },
  { type: 'slider', name: 'Slider / Carousel', variants: ['Full Width Hero Slider', 'Content + Image Slider', 'Card Carousel', 'Thumbnail Slider', 'Testimonial Slider', 'Capability Spotlight', 'Service Story Carousel'], tiers: 'All tiers', enabled: true },
  { type: 'cta', name: 'CTA Banner', variants: ['Centered High Impact', 'Split Dual Action', 'Floating Elevated Card', 'Gradient Glow', 'Lead Capture Form', 'Simple Contact Banner', 'Guided Next Step'], tiers: 'All tiers', enabled: true },
  { type: 'footer', name: 'Footer', variants: ['Standard 4-Column', 'Minimal Clean Bar', 'Newsletter Subscription', 'App & Status Dashboard', 'Corporate Legal Bar', 'Company Link Columns', 'Compact Brand Footer'], tiers: 'All tiers', enabled: true },
  { type: 'products', name: 'Our Products', variants: ['Product Grid', 'Featured Spotlight', 'Category Tabs', 'Pricing Cards', 'Compact List', 'Comparison Table', 'Full Catalog'], tiers: 'All tiers', enabled: true },
];

export const STARTER_SECTION_OPTIONS = [
  ['header', 'Header'], ['hero', 'Hero'], ['features', 'Features'], ['why-us', 'Why Choose Us'], ['pricing', 'Pricing'], ['testimonials', 'Testimonials'],
  ['trust', 'Trust Logos'], ['faq', 'FAQs'], ['blog', 'Blogs'], ['slider', 'Slider / Carousel'],
  ['cta', 'CTA'], ['footer', 'Footer'], ['products', 'Our Products'],
];

// ── LocalStorage readers ──────────────────────────────────────────────────────
export const readStoredAdminSettings = () => {
  try {
    return JSON.parse(localStorage.getItem('cloudsetu_admin_global_settings') || '{}');
  } catch {
    return {};
  }
};

export const readBuilderVisibilitySettings = () => {
  try {
    return JSON.parse(localStorage.getItem('cloudsetu_admin_settings') || '{}');
  } catch {
    return {};
  }
};

export const readBuilderVisibility = () => readBuilderVisibilitySettings().showRightEditorInBuilder !== false;

// ── Image optimization ────────────────────────────────────────────────────────
export const optimizeUploadedCardImage = file => new Promise((resolve, reject) => {
  if (!file.type.startsWith('image/')) {
    reject(new Error('Choose an image file.'));
    return;
  }
  if (file.size > 5 * 1024 * 1024) {
    reject(new Error('Image files must be 5 MB or smaller.'));
    return;
  }

  const reader = new FileReader();
  reader.onerror = () => reject(new Error('The image file could not be read.'));
  reader.onload = () => {
    const image = new Image();
    image.onerror = () => reject(new Error('The selected file is not a valid image.'));
    image.onload = () => {
      try {
        const scale = Math.min(1, 1400 / Math.max(image.naturalWidth, image.naturalHeight));
        const canvas = document.createElement('canvas');
        canvas.width = Math.max(1, Math.round(image.naturalWidth * scale));
        canvas.height = Math.max(1, Math.round(image.naturalHeight * scale));
        const context = canvas.getContext('2d');
        if (!context) {
          reject(new Error('Image processing is unavailable in this browser.'));
          return;
        }
        context.drawImage(image, 0, 0, canvas.width, canvas.height);
        const optimized = canvas.toDataURL('image/webp', 0.82);
        if (optimized.length > 1_100_000) {
          reject(new Error('The optimized image is still too large to save in this browser.'));
          return;
        }
        resolve(optimized);
      } catch (error) {
        reject(error);
      }
    };
    image.src = String(reader.result || '');
  };
  reader.readAsDataURL(file);
});

// ── Template page builders ────────────────────────────────────────────────────
export const createAdminTemplatePages = (sharedContent, library) => Object.fromEntries(
  DEFAULT_SITE_DESIGN_NAMES.map((_, pageIndex) => [
    pageIndex,
    createSiteTemplatePage(pageIndex, library, sharedContent),
  ])
);

export const readAdminTemplatePages = (sharedContent, sections) => {
  try {
    const saved = JSON.parse(localStorage.getItem('cloudsetu_admin_page_builder') || 'null');
    const defaultPages = createAdminTemplatePages(sharedContent, sections);
    if (!saved?.pages || typeof saved.pages !== 'object') return defaultPages;
    const savedPages = saved.pages;
  const pageIndices = Object.keys(savedPages)
      .filter(index => Number.isInteger(Number(index)) && Number(index) >= 0)
      .sort((left, right) => Number(left) - Number(right));
    return Object.fromEntries(pageIndices.map(pageIndex => {
      const currentPage = savedPages[pageIndex];
      const defaultPage = defaultPages[pageIndex];
      const rawSections = Array.isArray(currentPage?.sections)
        ? currentPage.sections
        : Array.isArray(currentPage)
          ? currentPage
          : defaultPage?.sections || [];
      const sectionsWithSharedContent = rawSections.map(section => {
        const { contentOverrides, ...layout } = section;
        const content = sharedContent[section.type] || DEFAULT_SHARED_STOREFRONT_CONTENT[section.type] || {};
        return {
          ...layout,
          heading: content.heading || '',
          subtext: content.subtext || '',
          primaryText: content.primaryText || '',
          secondaryText: content.secondaryText || '',
          kicker: content.kicker || '',
          mediaUrl: content.mediaUrl || '',
          mediaAlt: content.mediaAlt || '',
          cards: content.cards || [],
          navigation: content.navigation || [],
          actions: content.actions || [],
        };
      });
      if (!sectionsWithSharedContent.some(section => section.type === 'header')) {
        const defaultHeader = defaultPage?.sections?.find(section => section.type === 'header');
        if (defaultHeader) sectionsWithSharedContent.unshift(defaultHeader);
      }
      return [pageIndex, {
        ...(defaultPage || {}),
        ...(Array.isArray(currentPage) ? {} : currentPage),
        name: currentPage?.name || defaultPage?.name || `Site ${Number(pageIndex) + 1}`,
        sections: sectionsWithSharedContent,
      }];
    }));
  } catch {
    return createAdminTemplatePages(sharedContent, sections);
  }
};
